import React, { useEffect, useState } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Alert, Empty, Input, Select, Switch, Table, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  useLazyGetChannelsQuery,
  useLazyGetChannelsByServerQuery,
  usePostChannelMutation,
  usePutChannelMutation,
} from "../../../redux/channels/channelAPI";
import {
  updateFormAction,
  updateOpenDrawerChannel,
  updateChannel,
} from "../../../redux/channels/channelSlice";
import { useLazyGetServersQuery } from "../../../redux/servers/serverAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import ChannelsTableActions from "./ChannelsTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
import FormularyHeaderInputText from "../../components/formularyHeader/FormularyHeaderInputText";
import FormularyHeaderInputNumber from "../../components/formularyHeader/FormularyHeaderInputNumber";
import FormularyHeaderInputSelectString from "../../components/formularyHeader/FormularyHeaderInputSelectString";
import FormularyHeaderInputSelectKeyValue from "../../components/formularyHeader/FormularyHeaderInputSelectKeyValue";
import FormularyHeaderActionsEdit from "../../components/formularyHeader/FormularyHeaderActionsEdit";
// UTILS
import { validateIpAndPortString, getTableRowClass } from "../../../utils/utils";
import { channelSocketsValid } from "./channelFormValidity";
// OPC UA endpoint helpers are the wizard's — it configured this field first. They are
// reused rather than re-implemented so the two screens cannot disagree about what a
// valid `opc.tcp://host:port` is, or about which spelling of the field a record carries.
import {
  isValidOpcUaEndpointUrl,
  resolveChannelOpcUaEndpointUrl,
} from "../../setupWizard/steps/StepChannels";

// INTERFACES
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/EdgeManagement.interface";
import { ChannelInterface } from "../../../interfaces/Channel.interface";
import { ChannelType as FbChannelType } from "../../../flatbuffers/generated/archive/channel-type";
import { formatUuidT, channelTypeLabel } from "../../../flatbuffers/display";
import { FormAction } from "../../../types/enums";
// CONSTANTS
import { BLANK_CHANNEL } from "../../../constants/channels";
import {
  CHANNEL_TYPE_SINGLE,
  CHANNEL_TYPE_ROUTABLE,
  channelTypeFromRoutable,
  routableFromChannelType,
} from "../../../constants/channelRouting";

const getUdpSrc = (addr: string): string => {
  const parts = addr.split(":");
  return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : "";
};

const getUdpDst = (addr: string): string => {
  const parts = addr.split(":");
  return parts.length >= 4 ? `${parts[2]}:${parts[3]}` : "";
};

/**
 * A channel's OPC UA endpoint as a string ready for the wire.
 *
 * Two jobs, both load-bearing:
 *
 * 1. A row loaded from GET /channels may carry the value as raw UTF-8 bytes
 *    rather than a JS string. The serializer reads it with `String(v)`, which
 *    would stringify a `Uint8Array` as "111,222,…" and save that as the
 *    endpoint. `resolveChannelOpcUaEndpointUrl` decodes either spelling.
 * 2. Trimming means whitespace-only input becomes `""` and never goes out as a
 *    bogus `"   "` endpoint.
 *
 * Returning `""` for an absent value is deliberate and is NOT the same as
 * dropping the key: `dualNullable` keeps an explicit `""` as `""` so a
 * deliberate clear reaches the server as a value it can write, while an absent
 * or null key packs as an absent string. Every body this screen builds — the
 * create body, the edit PUT and the Active toggle — goes through here so the
 * three cannot diverge on that distinction.
 */
function normalizedOpcUaEndpoint(channel: ChannelInterface): string {
  return resolveChannelOpcUaEndpointUrl(channel).trim();
}

/**
 * Builds the channel-create body from the inline create form's `channel` state.
 *
 * The key spellings are the ones the `CreateChannel` serializer READS, not the
 * snake_case names LiftCloud's JSON routes use. POST /channels is a FlatBuffer
 * endpoint (`extraOptions: { fbSerializer: "CreateChannel" }` in channelAPI.ts)
 * and that serializer looks up `serverId`, `connectionType`, `baudRate`,
 * `*SocketAddress` and `heartbeatFrequency` by exact key. A snake_case body
 * therefore reads as absent on every one of them: `baudRate` fell back to 9600,
 * `connectionType` to 0, the addresses to null, and `serverId` — a required
 * struct in archive/channel.fbs — packed as null, so `Channel.pack()` threw
 * `Required field "server_id" is not set` inside flatBufferBaseQuery and the
 * request was turned into a CUSTOM_ERROR without a single byte going out.
 *
 * `StepChannels.tsx`'s `buildChannelBody` is the working reference for this
 * shape; keep the two in step when a field is added to either.
 */
export function buildChannelPostBody(
  channel: ChannelInterface,
  organizationId: string | null,
): Record<string, unknown> {
  return {
    active: true,
    routable: channel.routable,
    // Bank attach/detach has no FlatBuffer representation — Archive.Channel has
    // no banks field and UnpackChannelRequest never populates them — so these
    // stay as the empty pair the JSON route expects (an empty banks2delete
    // passes LiftCloud's detach rejection, a non-empty one is refused outright).
    banks: [],
    banks2delete: [],
    index: channel.index,
    name: channel.name,
    type: channel.type,
    connectionType: channel.connectionType,
    serverId: channel.serverId,
    // `organization_id` is a required struct in archive/channel.fbs, so it has
    // to pack to a real UUID or the whole request throws before it is sent.
    // LiftCloud derives the channel's organization from the resolved server row
    // and ignores this value; the loaded channel wins when it has one (the
    // inline form starts from a clone of the selected row), the page's own org
    // scope otherwise.
    organizationId: channel.organizationId ?? organizationId,
    baudRate: channel.baudRate,
    routableSocketAddress: channel.routableSocketAddress,
    vendorSocketAddress: channel.vendorSocketAddress,
    reportingSocketAddress: channel.reportingSocketAddress,
    heartbeatFrequency: channel.heartbeatFrequency,
    // Read by `CreateChannel` as `dualNullable(body, "opc_ua_endpoint_url",
    // "opcUaEndpointUrl")`, so the camelCase spelling is the one that lands.
    // Carried here for the same reason as `putChannel`: without it a channel
    // created from this screen can never name its OPC UA endpoint.
    opcUaEndpointUrl: normalizedOpcUaEndpoint(channel),
  };
}

/**
 * Builds the whole-record PUT body both save paths send.
 *
 * `putChannel` (save the edited row) and `toggleChannelActive` (flip Status in
 * place) both PUT a clone of the ENTIRE record, so this is the one place that
 * decides what the endpoint field means on the wire for either of them. Shared
 * rather than duplicated on purpose: the Active toggle never mentions the
 * endpoint, and it is exactly that click which used to blank an OPC UA endpoint
 * that had been configured correctly — the PUT carried no readable value, the
 * serializer packed an absent string, and LiftCloud wrote the column blank.
 *
 * `banks`/`server` are GORM preload relations with no FlatBuffer
 * representation; sending them would only put unreferenced objects in front of
 * the endpoint's own pack() call.
 */
export function buildChannelPutBody(source: ChannelInterface): ChannelInterface {
  // Read the endpoint BEFORE cloning. The clone exists so this builder cannot
  // mutate the caller's row; a value that is already a plain string survives it
  // in any realm, whereas decoding after the clone asks `instanceof Uint8Array`
  // of a deserialized typed array — false under jsdom, which would silently
  // blank a byte-encoded endpoint instead of preserving it.
  const opcUaEndpointUrl = normalizedOpcUaEndpoint(source);
  const copy: ChannelInterface = structuredClone(source);
  copy.opcUaEndpointUrl = opcUaEndpointUrl;
  delete copy.banks;
  delete copy.server;
  return copy;
}

function ChannelsTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Column, ColumnGroup } = Table;
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const { theme } = useSelector((state: RootState) => state.home);
  const location = useLocation();
  const idServer = new URLSearchParams(location.search).get("idServer");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [channels, setChannels] = useState<ChannelInterface[]>([]);
  const [servers, setServers] = useState<ServerInterface[]>([]);
  const [channel, setChannel] = useState<ChannelInterface>({
    ...BLANK_CHANNEL,
  });
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const {
    channelRefreshTable,
    formAction,
    channel: selectedChannel,
  } = useSelector((state: RootState) => state.channel);
  const { userLogged, globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetChannelsTable,
    {
      data: dataChannels,
      isLoading: isLoadingChannels,
      isError: isErrorChannels,
      isFetching: isFetchingChannels,
    },
  ] = useLazyGetChannelsQuery();

  const [
    triggerGetChannelsByServerTable,
    {
      data: dataChannelsByServer,
      isLoading: isLoadingChannelsByServer,
      isError: isErrorChannelsByServer,
      isFetching: isFetchingChannelsByServer,
    },
  ] = useLazyGetChannelsByServerQuery();

  const [triggerGetServers, { data: dataGetServers, isLoading: isLoadingGetServers }] =
    useLazyGetServersQuery();

  const [
    triggerPostChannel,
    {
      isSuccess: isSuccessPostChannel,
      isLoading: isLoadingPostChannel,
      isError: isErrorPostChannel,
      error: errorPostChannel,
      reset: resetPostChannel,
    },
  ] = usePostChannelMutation();

  const [
    triggerPutChannel,
    {
      isSuccess: isSuccessPutChannel,
      isLoading: isLoadingPutChannel,
      isError: isErrorPutChannel,
      error: errorPutChannel,
      reset: resetPutChannel,
    },
  ] = usePutChannelMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getServers = async () => {
    const token = await GETJwtToken();
    triggerGetServers({
      token,
      page: 1,
      limit: 9999,
    });
  };

  const getData = async (page: number, limit: number) => {
    let searchAndFilterString = "";
    if (
      userLogged &&
      userLogged.organizationId &&
      userLogged.organizationId !== globalOrganizationId
    ) {
      searchAndFilterString = searchAndFilterString.concat(`&org_id=${globalOrganizationId}`);
    }
    const token = await GETJwtToken();
    if (idServer && idServer !== "") {
      triggerGetChannelsByServerTable({
        page,
        limit,
        token,
        serverId: idServer,
      });
    } else {
      triggerGetChannelsTable({
        page,
        limit,
        token,
        orgId: userLogged.org.id,
        searchAndFilterString,
      });
    }
  };

  const handleTableChange = (pagination_: any) => {
    setPagination({
      ...pagination,
      current: pagination_.current,
      pageSize: pagination_.pageSize,
      total: pagination_.total,
    });
    getData(pagination_.current, pagination_.pageSize);
  };

  const refreshTable = async () => {
    setPagination({
      ...pagination,
      current: 1,
      pageSize: 10,
    });
    getData(1, 10);
    getServers();
  };

  const onChangeFormValue = (newValue: string | number | null, variableName: string) => {
    if (newValue === null) return;
    const copy: ChannelInterface = structuredClone(channel);
    if (variableName === "serverId" && typeof newValue === "string") {
      copy.serverId = newValue as any;
    } else if (variableName === "name" && typeof newValue === "string") {
      copy.name = newValue;
    } else if (variableName === "index" && typeof newValue === "string") {
      copy.index = newValue;
    } else if (variableName === "routableSocketAddress" && typeof newValue === "string") {
      copy.routableSocketAddress = newValue;
    } else if (variableName === "vendorSocketAddress" && typeof newValue === "string") {
      copy.vendorSocketAddress = newValue;
    } else if (variableName === "reportingSocketAddress" && typeof newValue === "string") {
      copy.reportingSocketAddress = newValue;
    } else if (variableName === "opcUaEndpointUrl" && typeof newValue === "string") {
      // Trimmed on the way in so a blanked field holds `""` — the value the
      // serializer forwards as a deliberate clear — and a whitespace-only field
      // can never be sent as a `"   "` endpoint.
      copy.opcUaEndpointUrl = newValue.trim();
    } else if (
      variableName === "connectionType" &&
      (newValue === FbChannelType.TCP ||
        newValue === FbChannelType.SERIAL ||
        newValue === FbChannelType.UDP)
    ) {
      copy.connectionType = newValue;
    } else if (variableName === "type" && typeof newValue === "number") {
      copy.type = newValue as FbChannelType;
    } else if (variableName === "heartbeatFrequency" && typeof newValue === "number") {
      copy.heartbeatFrequency = newValue;
    } else if (variableName === "baudRate" && typeof newValue === "number") {
      copy.baudRate = newValue;
    } else if (variableName === "udp_src_address" && typeof newValue === "string") {
      const dst = getUdpDst(String(copy.routableSocketAddress || ""));
      copy.routableSocketAddress = dst ? `${newValue}:${dst}` : newValue;
    } else if (variableName === "udp_dst_address" && typeof newValue === "string") {
      const src = getUdpSrc(String(copy.routableSocketAddress || ""));
      copy.routableSocketAddress = src ? `${src}:${newValue}` : newValue;
    }
    setChannel(copy);
  };

  const onChangeFormValueBoolean = (newValue: boolean, variableName: string) => {
    const copy: ChannelInterface = structuredClone(channel);
    if (variableName === "active") {
      copy.active = newValue;
    } else if (variableName === "routable") {
      copy.routable = newValue;
    }
    setChannel(copy);
  };

  // Channel Type (Single / Routable) cell for the channels table. Routable is
  // surfaced as a selectable type on the row being created/edited, and as a
  // read-only label on every other row. Backed by the `routable` bool.
  const channelTypeSelectOptions = [
    { label: t("edgeManagement.channels.single"), value: CHANNEL_TYPE_SINGLE },
    { label: t("edgeManagement.channels.routable"), value: CHANNEL_TYPE_ROUTABLE },
  ];
  const renderChannelTypeCell = (routable: boolean, record: ChannelInterface) => {
    const isEditingThisRow = formAction === "edit" && String(record.id) === String(channel.id);
    if (isEditingThisRow) {
      return (
        <Select
          size="small"
          style={{ width: 120 }}
          value={channelTypeFromRoutable(channel.routable)}
          onChange={(val: string) =>
            onChangeFormValueBoolean(routableFromChannelType(val), "routable")
          }
          options={channelTypeSelectOptions}
        />
      );
    }
    const label = routable
      ? t("edgeManagement.channels.routable")
      : t("edgeManagement.channels.single");
    return <span>{label}</span>;
  };

  // The endpoint as the form holds it, and whether what has been typed is
  // malformed. A malformed value is flagged on the field exactly the way the
  // IP:Port columns flag a bad socket (`status="error"`), and deliberately does
  // NOT block the save: this screen has no adapter-protocol context, so it
  // cannot tell whether a channel backs an OPC UA adapter at all, and the field
  // is optional for every other channel.
  const opcUaEndpointValue = normalizedOpcUaEndpoint(channel);
  const opcUaEndpointIsInvalid =
    opcUaEndpointValue !== "" && !isValidOpcUaEndpointUrl(opcUaEndpointValue);

  const checkFormIsValid = () => {
    const socketsValid = channelSocketsValid(channel);
    const reportingValid =
      !channel.reportingSocketAddress ||
      validateIpAndPortString(String(channel.reportingSocketAddress));
    if (
      channel.index !== "" &&
      channel.name !== "" &&
      socketsValid &&
      reportingValid &&
      (channel.heartbeatFrequency ? channel.heartbeatFrequency > 0 : true) &&
      channel.serverId != null
    ) {
      return false;
    }
    return true;
  };

  const postChannel = async () => {
    const token = await GETJwtToken();
    triggerPostChannel({ token, body: buildChannelPostBody(channel, globalOrganizationId) });
  };

  const putChannel = async () => {
    const token = await GETJwtToken();
    triggerPutChannel({ token, body: buildChannelPutBody(channel), id: channel.id });
  };

  const onClickSave = async () => {
    if (formAction === "create") {
      postChannel();
    } else if (formAction === "edit") {
      putChannel();
    }
  };

  const onClickResetForm = () => {
    resetPostChannel();
    resetPutChannel();
    dispatch(updateFormAction(FormAction.Create));
    const copy: ChannelInterface = structuredClone(channel);
    copy.name = "";
    copy.vendorSocketAddress = "";
    setChannel(copy);
  };

  const generateIncrementableIndex = (total: number) => {
    const defaultSocketAddress: number = 30000 + total + 1;
    const defaultReportingSocketAddress: number = 40000 + total + 1;

    const copy: ChannelInterface = structuredClone(channel);
    copy.index = `${total + 1}`;
    copy.vendorSocketAddress = `127.0.0.1:${defaultSocketAddress}`;
    copy.reportingSocketAddress = `127.0.0.1:${defaultReportingSocketAddress}`;
    if (idServer && idServer !== "") {
      copy.serverId = idServer as any;
    }
    setChannel(copy);
  };

  const onClickOpenChannelInformation = (record: ChannelInterface) => {
    if (record) {
      dispatch(updateChannel(record));
      dispatch(updateOpenDrawerChannel(true));
    }
  };

  const toggleChannelActive = async (record: ChannelInterface) => {
    const token = await GETJwtToken();
    // Same builder as the edit save, so a click that only flips Status cannot
    // send a body that reads differently from the one the editor sends —
    // see `buildChannelPutBody`.
    const copy = buildChannelPutBody(record);
    copy.active = !copy.active;
    triggerPutChannel({ token, body: copy, id: copy.id });
  };

  const getInputValidationType = (connectionType: FbChannelType | string | number) => {
    if (connectionType === FbChannelType.TCP) {
      return "ipAndPort";
    }
    if (connectionType === FbChannelType.UDP) {
      return "doubleIpAndPort";
    }
    if (connectionType === FbChannelType.SERIAL) {
      return "stringIpAndPort";
    }
    return "ip";
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    const newData = structuredClone(dataChannels || dataChannelsByServer);
    if (newData && newData.data) {
      setPagination({
        ...pagination,
        total: newData.total,
      });
      setChannels(newData.data);
      generateIncrementableIndex(newData.total || 0);
    }
  }, [dataChannels, dataChannelsByServer]);

  useEffect(() => {
    if (globalOrganizationId && globalOrganizationId !== "") {
      getData(pagination.current, pagination.pageSize);
      getServers();
    }
  }, [globalOrganizationId, idServer]);

  useEffect(() => {
    if (channelRefreshTable) {
      refreshTable();
    }
  }, [channelRefreshTable]);

  useEffect(() => {
    if (isErrorChannels || isErrorChannelsByServer) {
      setChannels([]);
    }
  }, [isErrorChannels, isErrorChannelsByServer]);

  useEffect(() => {
    if (dataGetServers && dataGetServers.data) {
      setServers(dataGetServers.data);
    }
  }, [dataGetServers]);

  useEffect(() => {
    if (selectedChannel && selectedChannel.id && String(selectedChannel.id) !== "") {
      setChannel({ ...selectedChannel });
    }
  }, [selectedChannel]);

  useEffect(() => {
    if (isSuccessPostChannel || isSuccessPutChannel || isErrorPostChannel || isErrorPutChannel) {
      onClickResetForm();
      refreshTable();
    }
  }, [isSuccessPostChannel, isSuccessPutChannel, isErrorPostChannel, isErrorPutChannel]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="mt-3">
      <>
        {hasPermission(Permission.ChannelsView, permissionSet ?? []) ? (
          <>
            {!isErrorChannels ? (
              <Table
                rowClassName={(record, index) => getTableRowClass(index, theme)}
                className="customTable"
                dataSource={channels}
                loading={
                  isLoadingChannels ||
                  isFetchingChannels ||
                  isLoadingChannelsByServer ||
                  isFetchingChannelsByServer ||
                  isLoadingPostChannel ||
                  isLoadingPutChannel ||
                  isLoadingGetServers
                }
                rowKey={(record) => formatUuidT(record.id)}
                size="small"
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 900 }}
              >
                {/** SERVER */}
                {FormularyHeaderInputSelectKeyValue({
                  columnName: t("general.server"),
                  inputPlaceholder: t("general.server"),
                  formAction,
                  value: channel.serverId ? String(channel.serverId) : "",
                  onChangeFormValue,
                  variableName: "serverId",
                  itemId: String(channel.id),
                  values: servers,
                  onClickCellName: onClickOpenChannelInformation,
                  enabledCellClick: true,
                  disableCreateRow: true,
                })}
                {/** NAME */}
                {FormularyHeaderInputText({
                  columnName: t("general.name"),
                  inputPlaceholder: t("general.name"),
                  formAction,
                  value: String(channel.name ?? ""),
                  onChangeFormValue,
                  variableName: "name",
                  itemId: String(channel.id),
                  onClickCellName: onClickOpenChannelInformation,
                  enabledCellClick: true,
                  disableCreateRow: true,
                })}
                {/** INDEX */}
                {FormularyHeaderInputText({
                  columnName: "Index",
                  inputPlaceholder: "Index",
                  formAction,
                  value: String(channel.index ?? ""),
                  onChangeFormValue,
                  variableName: "index",
                  itemId: String(channel.id),
                  onClickCellName: onClickOpenChannelInformation,
                  enabledCellClick: true,
                  disableCreateRow: true,
                })}
                {/** CONNECTION TYPE — render the enum value as a readable label */}
                {/* (TCP/SERIAL/UDP/ADAPTER/TCP_BOT/HTTP) instead of a raw int. */}
                {FormularyHeaderInputSelectString({
                  columnName: t("general.connectionType"),
                  inputPlaceholder: t("general.connectionType"),
                  formAction,
                  value: String(channel.connectionType),
                  onChangeFormValue,
                  variableName: "connectionType",
                  itemId: String(channel.id),
                  values: [
                    String(FbChannelType.TCP),
                    String(FbChannelType.SERIAL),
                    String(FbChannelType.UDP),
                  ],
                  onClickCellName: onClickOpenChannelInformation,
                  enabledCellClick: true,
                  disableCreateRow: true,
                  renderDisplay: (text) => {
                    if (text === null || text === undefined || text === "") return "--";
                    const asNumber = typeof text === "number" ? text : Number(text);
                    if (Number.isFinite(asNumber)) {
                      return channelTypeLabel(asNumber as FbChannelType);
                    }
                    return String(text);
                  },
                })}
                {/** SOCKET ADDRESS — transport-aware */}
                <ColumnGroup
                  title={(() => {
                    if (channel.connectionType === FbChannelType.UDP) return "Controller / Edge";
                    if (channel.connectionType === FbChannelType.SERIAL) return "Device Path";
                    return t("general.socketAddress");
                  })()}
                >
                  <Column
                    dataIndex="routable_socket_address"
                    key="routable_socket_address"
                    title={
                      channel.connectionType === FbChannelType.UDP ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          <Tooltip title="Controller IP:Port — source address (where data comes from)">
                            <Input
                              size="small"
                              placeholder="Controller IP:Port"
                              value={
                                formAction === "create"
                                  ? getUdpSrc(String(channel.routableSocketAddress ?? ""))
                                  : ""
                              }
                              disabled={formAction === "edit"}
                              onChange={(e) =>
                                onChangeFormValue(e.currentTarget.value, "udp_src_address")
                              }
                              status={
                                getUdpSrc(String(channel.routableSocketAddress ?? "")) &&
                                !validateIpAndPortString(
                                  getUdpSrc(String(channel.routableSocketAddress ?? "")),
                                )
                                  ? "error"
                                  : ""
                              }
                            />
                          </Tooltip>
                          <Tooltip title="Edge IP:Port — destination address (where data goes)">
                            <Input
                              size="small"
                              placeholder="Edge IP:Port"
                              value={
                                formAction === "create"
                                  ? getUdpDst(String(channel.routableSocketAddress ?? ""))
                                  : ""
                              }
                              disabled={formAction === "edit"}
                              onChange={(e) =>
                                onChangeFormValue(e.currentTarget.value, "udp_dst_address")
                              }
                              status={
                                getUdpDst(String(channel.routableSocketAddress ?? "")) &&
                                !validateIpAndPortString(
                                  getUdpDst(String(channel.routableSocketAddress ?? "")),
                                )
                                  ? "error"
                                  : ""
                              }
                            />
                          </Tooltip>
                        </div>
                      ) : (
                        <Input
                          size="small"
                          placeholder={
                            channel.connectionType === FbChannelType.SERIAL
                              ? "e.g. /dev/ttyUSB0"
                              : "IP:Port (e.g. 192.168.1.1:4000)"
                          }
                          value={
                            formAction === "create"
                              ? String(channel.routableSocketAddress ?? "")
                              : ""
                          }
                          disabled={formAction === "edit"}
                          onChange={(e) =>
                            onChangeFormValue(e.currentTarget.value, "routable_socket_address")
                          }
                          status={
                            channel.connectionType === FbChannelType.TCP &&
                            channel.routableSocketAddress &&
                            !validateIpAndPortString(String(channel.routableSocketAddress))
                              ? "error"
                              : ""
                          }
                        />
                      )
                    }
                    render={(text: string, record: ChannelInterface) => {
                      const isEditing =
                        formAction === "edit" && String(record.id) === String(channel.id);
                      if (isEditing && channel.connectionType === FbChannelType.UDP) {
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                            }}
                          >
                            <Tooltip title="Controller IP:Port — source address (where data comes from)">
                              <Input
                                size="small"
                                placeholder="Controller IP:Port"
                                value={getUdpSrc(String(channel.routableSocketAddress ?? ""))}
                                onChange={(e) =>
                                  onChangeFormValue(e.currentTarget.value, "udp_src_address")
                                }
                                status={
                                  getUdpSrc(String(channel.routableSocketAddress ?? "")) &&
                                  !validateIpAndPortString(
                                    getUdpSrc(String(channel.routableSocketAddress ?? "")),
                                  )
                                    ? "error"
                                    : ""
                                }
                              />
                            </Tooltip>
                            <Tooltip title="Edge IP:Port — destination address (where data goes)">
                              <Input
                                size="small"
                                placeholder="Edge IP:Port"
                                value={getUdpDst(String(channel.routableSocketAddress ?? ""))}
                                onChange={(e) =>
                                  onChangeFormValue(e.currentTarget.value, "udp_dst_address")
                                }
                                status={
                                  getUdpDst(String(channel.routableSocketAddress ?? "")) &&
                                  !validateIpAndPortString(
                                    getUdpDst(String(channel.routableSocketAddress ?? "")),
                                  )
                                    ? "error"
                                    : ""
                                }
                              />
                            </Tooltip>
                          </div>
                        );
                      }
                      if (isEditing) {
                        return (
                          <Input
                            size="small"
                            placeholder={
                              channel.connectionType === FbChannelType.SERIAL
                                ? "e.g. /dev/ttyUSB0"
                                : "IP:Port (e.g. 192.168.1.1:4000)"
                            }
                            value={String(channel.routableSocketAddress ?? "")}
                            onChange={(e) =>
                              onChangeFormValue(e.currentTarget.value, "routable_socket_address")
                            }
                            status={
                              channel.connectionType === FbChannelType.TCP &&
                              channel.routableSocketAddress &&
                              !validateIpAndPortString(String(channel.routableSocketAddress))
                                ? "error"
                                : ""
                            }
                          />
                        );
                      }
                      if (record.connectionType === FbChannelType.UDP) {
                        const src = getUdpSrc(text || "");
                        const dst = getUdpDst(text || "");
                        return (
                          <div>
                            <div>{src || "--"}</div>
                            <div style={{ color: "#6b7280" }}>→ {dst || "--"}</div>
                          </div>
                        );
                      }
                      return <div>{text || "--"}</div>;
                    }}
                  />
                </ColumnGroup>
                {/** VENDOR ADDRESS — hidden for UDP */}
                {channel.connectionType !== FbChannelType.UDP &&
                  FormularyHeaderInputText({
                    columnName:
                      channel.connectionType === FbChannelType.SERIAL
                        ? "Vendor Device Path"
                        : t("general.vendorSocketAddress"),
                    inputPlaceholder:
                      channel.connectionType === FbChannelType.SERIAL
                        ? "e.g. /dev/ttyUSB0"
                        : t("general.vendorSocketAddress"),
                    formAction,
                    value: String(channel.vendorSocketAddress ?? ""),
                    onChangeFormValue,
                    variableName: "vendorSocketAddress",
                    itemId: String(channel.id),
                    onClickCellName: onClickOpenChannelInformation,
                    enabledCellClick: true,
                    inputValidationType: getInputValidationType(channel.connectionType),
                    disableCreateRow: true,
                  })}
                {/**
                 * OPC UA ENDPOINT. For a channel backing an OPC UA adapter this
                 * is not one address among several: LiftCloud's
                 * `resolveOpcUaSocket` reads ONLY this field and ignores the
                 * socket columns entirely, so a PLC whose address changed after
                 * setup was unfixable from anywhere outside the wizard until
                 * this column existed. Optional for every other channel, and
                 * never a reason the save is disabled — see
                 * `opcUaEndpointIsInvalid`.
                 */}
                <ColumnGroup title={t("edgeManagement.channels.opcUaEndpointUrl")}>
                  <Column
                    dataIndex="opcUaEndpointUrl"
                    key="opcUaEndpointUrl"
                    title={
                      <Tooltip title="OPC UA endpoint the edge connects to (opc.tcp://host:port). This is the only address used for an OPC UA channel.">
                        <Input
                          size="small"
                          placeholder="opc.tcp://host:port"
                          value={formAction === "create" ? opcUaEndpointValue : ""}
                          disabled={formAction === "edit"}
                          onChange={(e) =>
                            onChangeFormValue(e.currentTarget.value, "opcUaEndpointUrl")
                          }
                          status={opcUaEndpointIsInvalid ? "error" : ""}
                        />
                      </Tooltip>
                    }
                    render={(_: unknown, record: ChannelInterface) => {
                      if (formAction === "edit" && String(record.id) === String(channel.id)) {
                        return (
                          <Input
                            size="small"
                            placeholder="opc.tcp://host:port"
                            value={opcUaEndpointValue}
                            onChange={(e) =>
                              onChangeFormValue(e.currentTarget.value, "opcUaEndpointUrl")
                            }
                            status={opcUaEndpointIsInvalid ? "error" : ""}
                          />
                        );
                      }
                      // Read tolerantly: a row may carry the value as raw UTF-8
                      // bytes rather than a string, so it goes through the same
                      // resolver the editor path uses instead of a bare `text`.
                      const url = resolveChannelOpcUaEndpointUrl(record);
                      if (!url) return <div>--</div>;
                      const valid = isValidOpcUaEndpointUrl(url);
                      return (
                        <div
                          style={valid ? undefined : { color: "#ff4d4f" }}
                          title={valid ? url : "Not a valid opc.tcp://host:port URL"}
                        >
                          {url}
                        </div>
                      );
                    }}
                  />
                </ColumnGroup>
                {/** REPORTING SOCKET ADDRESS */}
                {FormularyHeaderInputText({
                  columnName: t("general.reportingSocketAddress"),
                  inputPlaceholder: t("general.reportingSocketAddress"),
                  formAction,
                  value: String(channel.reportingSocketAddress ?? ""),
                  onChangeFormValue,
                  variableName: "reportingSocketAddress",
                  itemId: String(channel.id),
                  onClickCellName: onClickOpenChannelInformation,
                  enabledCellClick: true,
                  inputValidationType: "ipAndPort",
                  disableCreateRow: true,
                })}
                {/** HEARTBEAT FREQUENCY */}
                {FormularyHeaderInputNumber({
                  columnName: "Heartbeat",
                  inputPlaceholder: "Heartbeat",
                  formAction,
                  value: channel.heartbeatFrequency,
                  onChangeFormValue,
                  variableName: "heartbeatFrequency",
                  itemId: String(channel.id),
                  onClickCellName: onClickOpenChannelInformation,
                  enabledCellClick: true,
                  disableCreateRow: true,
                })}
                {/** BAUD RATE — SERIAL only */}
                {FormularyHeaderInputNumber({
                  columnName: "Baud Rate",
                  inputPlaceholder: "e.g. 9600",
                  formAction,
                  value: channel.baudRate,
                  onChangeFormValue,
                  variableName: "baudRate",
                  itemId: String(channel.id),
                  onClickCellName: onClickOpenChannelInformation,
                  enabledCellClick: true,
                  disableCreateRow: true,
                })}
                {/** STATUS — inline toggle, persists immediately */}
                <ColumnGroup title="Status">
                  <Column
                    title={
                      formAction === "create" ? (
                        <Switch
                          checked={channel.active}
                          onChange={(checked) => {
                            onChangeFormValueBoolean(checked, "active");
                          }}
                        />
                      ) : null
                    }
                    dataIndex="active"
                    key="active"
                    render={(active: boolean, record: ChannelInterface) => (
                      <div>
                        {formAction === "edit" && String(record.id) === String(channel.id) ? (
                          <Switch
                            checked={channel.active}
                            onChange={(checked) => {
                              onChangeFormValueBoolean(checked, "active");
                            }}
                          />
                        ) : (
                          <Switch
                            checked={active}
                            loading={
                              isLoadingPutChannel &&
                              formatUuidT(selectedChannel?.id) === formatUuidT(record.id)
                            }
                            onChange={() => {
                              toggleChannelActive(record);
                            }}
                          />
                        )}
                      </div>
                    )}
                  />
                </ColumnGroup>
                <ColumnGroup title={t("edgeManagement.channels.channelType")}>
                  <Column
                    title={
                      formAction === "create" ? (
                        <Select
                          size="small"
                          style={{ width: 120 }}
                          value={channelTypeFromRoutable(channel.routable)}
                          onChange={(val: string) =>
                            onChangeFormValueBoolean(routableFromChannelType(val), "routable")
                          }
                          options={channelTypeSelectOptions}
                        />
                      ) : null
                    }
                    dataIndex="routable"
                    key="routable"
                    render={(routable: boolean, record: ChannelInterface) =>
                      renderChannelTypeCell(routable, record)
                    }
                  />
                </ColumnGroup>
                {/** ACTIONS */}
                <ColumnGroup align="center" title={t("edgeManagement.banks.actions")}>
                  <Column
                    align="center"
                    title={t("edgeManagement.banks.actions")}
                    key="action"
                    render={(_: any, record: ChannelInterface) => (
                      <div>
                        {formAction === "edit" && String(record.id) === String(channel.id) ? (
                          <FormularyHeaderActionsEdit
                            formIsValid={checkFormIsValid()}
                            onClickSave={onClickSave}
                            onClickResetForm={onClickResetForm}
                          />
                        ) : (
                          <ChannelsTableActions selectedItem={record} />
                        )}
                      </div>
                    )}
                  />
                </ColumnGroup>
              </Table>
            ) : (
              <Alert message="errors" description="errors" type="error" showIcon className="mt-3" />
            )}
          </>
        ) : (
          <>
            <div className="generalStyles__noAccessToListTable">
              <Empty
                description={t("general.listNotAvailable")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          </>
        )}
      </>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostChannel}
        isSuccess={isSuccessPostChannel}
        requestType="POST"
        error={errorPostChannel}
        name="Channel"
      />
      <GlobalAlert2
        isError={isErrorPutChannel}
        isSuccess={isSuccessPutChannel}
        requestType="PUT"
        error={errorPutChannel}
        name="Channel"
      />
    </div>
  );
}

export default ChannelsTable;
